import React, {useEffect, useState} from "react";
import {
    Button,
    Checkbox,
    Fade, FormControl,
    FormControlLabel,
    IconButton,
    InputLabel, MenuItem,
    Select,
    Stack,
    TextField, Tooltip
} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon, CheckIcon, Copy01Icon, Delete02Icon, Maximize01Icon} from "@hugeicons/core-free-icons";
import Modal from "@mui/material/Modal";
import {green, grey} from "@mui/material/colors";
import {toast} from "react-toastify";
import {v4 as uuidv4} from "uuid";
import {Editor} from "@monaco-editor/react";
import {ParserModal} from "../../../../components/modals/ParserModal";
import {SystemPromptField} from "../../../../components/system-prompt/SystemPromptField";
import SchemaBuilderModal from "../../../../components/modals/SchemaBuilderModal"

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: "sm", width: "90%",
    maxHeight: "80vh",
    bgcolor: 'background.paper',
    borderRadius: 2,
    border: '0px solid #000',
    boxShadow: 24,
    px: 4,
    py: 3,
    overflowY: "auto"
};

export const CreateEntityModal = ({open, handleClose, entity, handleSubmit, onRemove, project, flow}) => {
    const [name, setName] = useState("");
    const [regex, setRegex] = useState("");
    const [systemPrompt, setSystemPrompt] = useState("");
    const [jsonSchema, setJsonSchema] = useState(``);
    const [entityType, setEntityType] = useState("FreeTextEntity");
    const [validation, setValidation] = useState(false);
    const [expandParser, setExpandParser] = useState(false);

    useEffect(() => {
        if (entity) {
            setValidation(entity.validation || false);
            setName(entity?.name || "");
            setRegex(entity?.regex || entity?.api_url || "");
            setEntityType(entity?.entityType || entity?.entity_type || "FreeTextEntity");
            setSystemPrompt(entity?.system_prompt || "");
            setJsonSchema(entity?.json_schema || ``);
        }
    }, [entity]);
    const onCreate = async () => {
        if (!name || (
            entityType === "RegexEntity" && validation && !regex
        )) {
            toast.error("All fields are required!");
            return;
        }
        if (!name || (
            entityType === "ApiRegexEntity" && validation && !regex
        )) {
            toast.error("All fields are required!");
            return;
        }
        let newEntity = {
            id: entity?.id || uuidv4(),
            name: name,
            entity_type: entityType,
            // Keep the legacy key until all deployed project mappers are updated.
            entityType: entityType,
            validation: validation,
        }
        if (entityType === "ApiRegexEntity" || entityType === "NEREntity") {
            newEntity.validation = validation;
            newEntity.api_url = regex;
        } else if (entityType === "RegexEntity") {
            newEntity.validation = validation;
            newEntity.regex = regex;
        } else if (entityType === "LLMExtractionBoostedEntity") {
            newEntity.system_prompt = systemPrompt;
            newEntity.json_schema = jsonSchema;
            delete newEntity.validation;
        } else if (entityType === "JSONSchemaEntity") {
            newEntity.system_prompt = systemPrompt;
            newEntity.json_schema = jsonSchema;
        }
        handleSubmit(newEntity);
        onClose();
    }

    const onClose = () => {
        setName("");
        setValidation(false);
        setRegex("");
        setEntityType("FreeTextEntity");
        handleClose();
        setSystemPrompt("");
        setJsonSchema("");
    }
    const [copied, setCopied] = useState(false);
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).catch(() => {
        });
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    return (
        <Modal
            open={open}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Fade in={open} timeout={500}>
                <Box>
                    <Box sx={{position: "fixed", top: 10, right: 10}}>
                        <IconButton
                            onClick={onClose}
                            color={"#FFF"}
                        >
                            <HugeiconsIcon icon={Cancel01Icon} color={grey[300]}/>
                        </IconButton>
                    </Box>

                    <Stack direction={"column"} gap={2} sx={style}>
                        <Typography variant={"h4"} fontWeight={"bold"} sx={{mb: 1}}>Create new entity</Typography>
                        <TextField
                            label={"Entity name"}
                            value={name}
                            error={name !== "" && project?.entities?.filter(i => i.name === name && entity?.id !== i.id).length > 0}
                            helperText={
                                name !== "" && project?.entities?.filter(i => i.name === name && entity?.id !== i.id).length > 0 ?
                                    "This name already exists."
                                    : "Entity name must be in lowercase and without spaces (_)"
                            }
                            onChange={e => setName(e.target.value?.replaceAll(" ", "_").toLowerCase())}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="entity-label">Entity Type</InputLabel>
                            <Select
                                labelId="entity-label"
                                id="entity-select"
                                value={entityType}
                                label="Entity Type"
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setEntityType(value);
                                    if (value === "FreeTextEntity") {
                                        setRegex("");
                                        setValidation(false);
                                    }
                                }}
                            >
                                <MenuItem value={"FreeTextEntity"}>FreeTextEntity</MenuItem>
                                <MenuItem value={"RegexEntity"}>RegexEntity</MenuItem>
                                <MenuItem value={"ApiRegexEntity"}>ApiRegexEntity</MenuItem>
                                <MenuItem value={"NEREntity"}>NEREntity</MenuItem>
                                <MenuItem value={"LLMExtractionBoostedEntity"}>LLMExtractionBoostedEntity</MenuItem>
                                <MenuItem value={"JSONSchemaEntity"}>JSONSchemaEntity</MenuItem>
                            </Select>
                        </FormControl>

                        {
                            entityType === "RegexEntity" &&
                            <>
                                <FormControlLabel control={
                                    <Checkbox
                                        size="medium"
                                        onChange={(e, value) => setValidation(value)}
                                        checked={validation}
                                    />} label={<Typography fontSize={13}>Validation</Typography>}/>
                                <TextField
                                    label={"Regex"}
                                    disabled={!validation}
                                    value={regex}
                                    helperText={"Enter your regex for validation"}
                                    onChange={e => setRegex(e.target.value)}
                                />
                            </>
                        }
                        {
                            entityType === "ApiRegexEntity" &&
                            <>
                                <FormControlLabel control={
                                    <Checkbox
                                        size="medium"
                                        onChange={(e, value) => setValidation(value)}
                                        checked={validation}
                                    />} label={<Typography fontSize={13}>Validation</Typography>}/>
                                <TextField
                                    label={"API url"}
                                    disabled={!validation}
                                    value={regex}
                                    helperText={"Enter your api url for validation"}
                                    onChange={e => setRegex(e.target.value)}
                                />
                            </>
                        }
                        {
                            entityType === "NEREntity" &&
                            <>
                                <FormControlLabel control={
                                    <Checkbox
                                        size="medium"
                                        onChange={(e, value) => setValidation(value)}
                                        checked={validation}
                                    />} label={<Typography fontSize={13}>Validation</Typography>}/>
                                <TextField
                                    label={"NER Entity"}
                                    disabled={!validation}
                                    value={regex}
                                    onChange={e => setRegex(e.target.value)}
                                />
                            </>
                        }
                        {
                            entityType === "LLMExtractionBoostedEntity" &&
                            <>
                                <SystemPromptField
                                    value={systemPrompt}
                                    onChange={(e) => setSystemPrompt(e.target.value)}
                                />
                                <Box>
                                    <ParserModal
                                        handleClose={() => setExpandParser(false)}
                                        open={expandParser}
                                        onSuccess={(content) => setJsonSchema(content || "")}
                                        text={jsonSchema}
                                    />
                                    <Stack direction={"row"} gap={1} justifyContent={"space-between"}
                                           alignItems={"center"} sx={{pb: 0.5}}>
                                        <Typography fontWeight={700}>JSON Schema</Typography>
                                        <Box>
                                            <IconButton onClick={() => setExpandParser(true)}>
                                                <HugeiconsIcon icon={Maximize01Icon} size={15}/>
                                            </IconButton>
                                        </Box>
                                    </Stack>
                                    <Editor
                                        height="200px"
                                        defaultLanguage="javascript"
                                        value={jsonSchema}
                                        // onChange={(value, e) => onChangeData(isHook ? "api_output_parser" : "parser", value)}
                                    />
                                    <Typography fontWeight={400} fontSize={10}>
                                        It’s like a blueprint that defines what a valid JSON object should look like —
                                        what fields it must have, their types, allowed values, and formats.
                                    </Typography>
                                </Box>
                            </>
                        }
                        {entityType === "JSONSchemaEntity" && (
                            <>
                                <SystemPromptField value={systemPrompt}
                                                   onChange={(e) => setSystemPrompt(e.target.value)}/>
                                <Box>
                                    <SchemaBuilderModal
                                        handleClose={() => setExpandParser(false)}
                                        open={expandParser}
                                        onSuccess={(content) => setJsonSchema(content || "")}
                                        text={jsonSchema}
                                    />
                                    <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center"
                                           sx={{pb: 0.5}}>
                                        <Typography fontWeight={700}>JSON Schema</Typography>
                                        <Stack direction="row" gap={0.5}>
                                            <Tooltip title={"Copy JSON schema"}>
                                            <IconButton onClick={()=>handleCopy(jsonSchema)}>
                                                {copied ? <HugeiconsIcon icon={CheckIcon} color={"green"} size={15}/>: 
                                                <HugeiconsIcon icon={Copy01Icon} size={15}/>}
                                            </IconButton>
                                            </Tooltip>
                                            <Tooltip title={"Open schema builder"}>
                                                <IconButton onClick={() => setExpandParser(true)}>
                                                    <HugeiconsIcon icon={Maximize01Icon} size={15}/>
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Stack>
                                        <Editor
                                            height="200px"
                                            defaultLanguage="json"
                                            value={jsonSchema}
                                            options={{ readOnly: true, minimap: { enabled: false }, fontSize: 11 }}
                                            onMount={(editor) => {
                                                editor.onMouseDown(() => setExpandParser(true));
                                            }}
                                        />
                                    <Typography fontWeight={400} fontSize={10} padding={1}>
                                        It's like a blueprint that defines what a valid JSON object should look like...
                                    </Typography>
                                </Box>
                            </>
                        )}

                        <Stack direction={'row'} gap={1} justifyContent={"flex-end"} useFlexGap flexWrap={"wrap"}>
                            <Button
                                variant={"ghost"}
                                color={"secondary"}
                                sx={{
                                    borderRadius: 1,
                                    textTransform: "capitalize",
                                    borderColor: "transparent",
                                    "&:hover": {
                                        borderColor: "transparent",
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                }}
                                onClick={onClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant={"modal"}
                                color={"secondary"}
                                sx={{borderRadius: 1, textTransform: "capitalize"}}
                                disabled={!name || project?.entities?.filter(i => i.name === name && entity?.id !== i.id).length > 0 || (entityType === "RegexEntity" && validation && !regex) || (entityType === "LLMExtractionBoostedEntity" && !systemPrompt && !jsonSchema) || (entityType === "JSONSchemaEntity"  && !jsonSchema)}
                                onClick={onCreate}
                            >
                                Save
                            </Button>
                            {
                                entity?.id &&
                                <Button
                                    variant={"outlined"}
                                    color={"error"}
                                    sx={{borderRadius: 1, textTransform: "capitalize", ml: "auto"}}
                                    onClick={() => {
                                        onRemove(entity?.id);
                                        onClose();
                                    }}
                                >
                                    <HugeiconsIcon icon={Delete02Icon} size={20}/>
                                </Button>
                            }
                        </Stack>
                    </Stack>
                </Box>
            </Fade>
        </Modal>
    )
}
